/** Client cho báo giá NCC dạng bảng tính (phần D). */
import { apiFetch } from '@/services/apiFetch'
import type { SheetGrid } from '@/features/suppliers/quoteSheetModel'

export type QuoteSheetDraft = {
  roles: Array<string | null>
  updated_by: string | null
  updated_at: string
  grid: SheetGrid
  /** Cờ hiện trên PO theo vị trí cột ở response (máy chủ đã áp mặc định nếu chưa lưu). */
  po_visible?: boolean[]
  /** Cột có ô nhập lúc mua: luôn hiện trên PO, không tắt được. */
  po_locked?: boolean[]
}

export type QuoteSheetCalc = {
  values: Record<string, string | boolean | null>
  errors: Array<{ cell: string; message: string }>
  formulaCount: number
  /** Dòng (1-based) có ô nhập lúc mua: chưa có kết quả cho tới khi người mua nhập trên PO. */
  pendingRows: number[]
}

async function readError(res: Response, fallback: string): Promise<Error> {
  const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null
  return new Error(body?.error?.message ?? `${fallback} (${res.status})`)
}

export async function getQuoteSheet(supplierId: string): Promise<QuoteSheetDraft | null> {
  const res = await apiFetch(`/api/suppliers/${encodeURIComponent(supplierId)}/quote-sheet`)
  if (!res.ok) throw await readError(res, 'Không tải được báo giá dạng bảng')
  const body = (await res.json()) as { sheet: QuoteSheetDraft | null }
  return body.sheet
}

export async function saveQuoteSheet(
  supplierId: string,
  payload: { roles: Array<string | null>; grid: SheetGrid; po_visible?: boolean[] | null },
): Promise<QuoteSheetDraft> {
  const res = await apiFetch(`/api/suppliers/${encodeURIComponent(supplierId)}/quote-sheet`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw await readError(res, 'Không lưu được báo giá dạng bảng')
  return ((await res.json()) as { sheet: QuoteSheetDraft }).sheet
}

export async function calculateQuoteSheet(
  supplierId: string,
  grid: SheetGrid,
  roles: Array<string | null> = [],
): Promise<QuoteSheetCalc> {
  const res = await apiFetch(`/api/suppliers/${encodeURIComponent(supplierId)}/quote-sheet/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ grid, roles }),
  })
  if (!res.ok) throw await readError(res, 'Không tính lại được báo giá')
  const body = (await res.json()) as QuoteSheetCalc & { ok: true }
  return { values: body.values, errors: body.errors, formulaCount: body.formulaCount, pendingRows: body.pendingRows ?? [] }
}

export type PublishPreviewLine = {
  row: number
  item_id: string | null
  item_source: 'chosen' | 'kept_from_active' | 'none'
  row_ref: string
  supplier_item_name: string
  uom: string
  unit_price: number
  old_unit_price: number | null
  diff: number | null
  item_linked: boolean
  /** Ô có giá trị của dòng (tên cột → giá trị): ánh xạ, khóa trên PO. */
  mappings: Record<string, unknown>
  /** Ô trống được công thức dùng: người mua nhập lúc mua. */
  input_names: string[]
}

export type PublishPreview = {
  /** Số báo giá đang áp dụng của NCC sẽ hết hiệu lực khi phát hành. */
  replaces_active_quotes?: number
  can_publish: boolean
  blocking: string[]
  row_errors: Array<{ row: number; message: string }>
  formula_count: number
  line_count: number
  unlinked_count: number
  kept_count: number
  new_count: number
  lines: PublishPreviewLine[]
}

export async function previewQuoteSheetPublish(
  supplierId: string,
  payload: { roles: Array<string | null>; grid: SheetGrid; uom_default: string; item_ids?: Record<string, string | null>; po_visible?: boolean[] | null },
): Promise<PublishPreview> {
  const res = await apiFetch(`/api/suppliers/${encodeURIComponent(supplierId)}/quote-sheet/preview-publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw await readError(res, 'Không xem trước được phát hành')
  return (await res.json()) as PublishPreview
}

/** Phát hành; khóa idempotency tạo mới mỗi lần bấm để không nhân đôi khi mạng chập chờn. */
export async function publishQuoteSheet(
  supplierId: string,
  payload: { roles: Array<string | null>; grid: SheetGrid; uom_default: string; item_ids?: Record<string, string | null>; po_visible?: boolean[] | null },
  idempotencyKey: string,
): Promise<{ quote_id: string | null; lines: number }> {
  const res = await apiFetch(`/api/suppliers/${encodeURIComponent(supplierId)}/quote-sheet/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw await readError(res, 'Không phát hành được báo giá')
  const body = (await res.json()) as { quote?: { id: string } | null }
  return { quote_id: body.quote?.id ?? null, lines: 0 }
}

export type QuoteSheetTemplate = { id: string; name: string; created_by: string | null; created_at: string }

export async function listQuoteSheetTemplates(): Promise<QuoteSheetTemplate[]> {
  const res = await apiFetch('/api/quote-sheet-templates')
  if (!res.ok) throw await readError(res, 'Không tải được danh sách mẫu')
  return ((await res.json()) as { templates: QuoteSheetTemplate[] }).templates
}

export async function saveQuoteSheetTemplate(
  name: string,
  payload: { roles: Array<string | null>; grid: SheetGrid; po_visible?: boolean[] | null },
): Promise<{ id: string; name: string }> {
  const res = await apiFetch('/api/quote-sheet-templates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, ...payload }),
  })
  if (!res.ok) throw await readError(res, 'Không lưu được mẫu')
  return ((await res.json()) as { template: { id: string; name: string } }).template
}

export async function deleteQuoteSheetTemplate(templateId: string): Promise<void> {
  const res = await apiFetch(`/api/quote-sheet-templates/${encodeURIComponent(templateId)}`, { method: 'DELETE' })
  if (!res.ok) throw await readError(res, 'Không xóa được mẫu')
}

export async function applyQuoteSheetTemplate(supplierId: string, templateId: string): Promise<void> {
  const res = await apiFetch(
    `/api/suppliers/${encodeURIComponent(supplierId)}/quote-sheet/from-template/${encodeURIComponent(templateId)}`,
    { method: 'POST' },
  )
  if (!res.ok) throw await readError(res, 'Không áp được mẫu')
}
